import { useState } from 'react';
import { useSuspenseQuery } from '@tanstack/react-query';
import { getLegacyReferralReportOptions, type LegacyReferralClinicianReportRow } from '@generated';
import { KpiCard } from '~/shared/components/KpiCard';
import { FilterBar } from '~/shared/components/filter-bar/FilterBar';
import { DataTable } from '~/shared/data-tables/DataTable';
import type { DataTableColumnProps } from '~/shared/data-tables/DataTableColumn';
import { DatePickerField } from '~/shared/forms/controls/base-fields/DatePickerField';
import { StatusPill } from '~/shared/StatusPill';
import { SuspenseWrapper } from '~/shared/SuspenseWrapper';
import { addDays, convertClientDateToServerDate } from '~/util/date';

function formatRate(rate?: number) {
	return rate === undefined || rate === null ? '-' : `${Math.round(rate * 100)}%`;
}

function formatMinutes(minutes?: number) {
	if (minutes === undefined || minutes === null) {
		return '-';
	}
	const rounded = Math.round(minutes);
	const days = Math.floor(rounded / 1440);
	const hours = Math.floor((rounded % 1440) / 60);
	const mins = rounded % 60;
	if (days > 0) {
		return `${days}d ${hours}h`;
	}
	return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
}

const columns: DataTableColumnProps<LegacyReferralClinicianReportRow>[] = [
	{
		header: 'Clinician',
		key: 'name',
		renderCell: x => (
			<div className="flex flex-col">
				<span>{x.name}</span>
				<span className="text-xs text-gray-500">{x.clinicalTypeAbbreviation}</span>
			</div>
		)
	},
	{
		header: 'Accepting',
		key: 'acceptingReferrals',
		renderCell: x => <StatusPill intent={x.acceptingReferrals ? 'success' : 'neutral'}>{x.acceptingReferrals ? 'Yes' : 'No'}</StatusPill>
	},
	{
		header: 'Home Zip',
		key: 'homeZipCode',
		renderCell: x => x.homeZipCode ?? '-'
	},
	{
		header: 'Coverage Zips',
		key: 'coverageZipCodes',
		cellClassName: 'max-w-64 whitespace-normal',
		renderCell: x => (x.coverageZipCodes.length > 0 ? x.coverageZipCodes.join(', ') : '-')
	},
	{ header: 'Alerted', key: 'alerted', cellAlign: 'right', headerAlign: 'right' },
	{ header: 'Accepted', key: 'accepted', cellAlign: 'right', headerAlign: 'right' },
	{ header: 'Declined', key: 'declined', cellAlign: 'right', headerAlign: 'right' },
	{ header: 'No Resp', key: 'noResponse', cellAlign: 'right', headerAlign: 'right' },
	{ header: 'Cancelled', key: 'cancelledBeforeResponse', cellAlign: 'right', headerAlign: 'right' },
	{
		header: 'Resp %',
		key: 'responseRate',
		cellAlign: 'right',
		headerAlign: 'right',
		renderCell: x => formatRate(x.responseRate)
	},
	{
		header: 'Accept %',
		key: 'acceptanceRate',
		cellAlign: 'right',
		headerAlign: 'right',
		renderCell: x => formatRate(x.acceptanceRate)
	},
	{
		header: 'Median Resp',
		key: 'medianResponseMinutes',
		cellAlign: 'right',
		headerAlign: 'right',
		renderCell: x => formatMinutes(x.medianResponseMinutes)
	}
];

function ReferralReportResults({ from, to }: { from: Date; to: Date }) {
	const { data: report } = useSuspenseQuery(getLegacyReferralReportOptions(convertClientDateToServerDate(from), convertClientDateToServerDate(to)));
	const { totals } = report;

	return (
		<div className="space-y-6">
			<div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
				<KpiCard label="Referrals Received" value={report.referralsReceived} subLabel={`${report.referralsPending} pending`} />
				<KpiCard
					label="Referral Acceptance"
					value={formatRate(report.referralAcceptanceRate)}
					subLabel={`${report.referralsAccepted} accepted / ${report.referralsNtfc} NTFC`}
				/>
				<KpiCard
					label="Clinician Acceptance"
					value={formatRate(totals.acceptanceRate)}
					subLabel={`${totals.accepted} accepted / ${totals.declined} declined of ${totals.alerted} alerts`}
				/>
				<KpiCard
					label="Median Response Time"
					value={formatMinutes(totals.medianResponseMinutes)}
					subLabel={`${formatRate(totals.responseRate)} responded, ${totals.noResponse} awaiting`}
				/>
			</div>
			<DataTable
				columns={columns}
				rows={report.clinicians}
				getRowId={x => x.userId!}
				emptyMessage="No clinicians alerted or accepting referrals"
			/>
		</div>
	);
}

export function ReferralReport() {
	const [from, setFrom] = useState<Date | null>(() => addDays(new Date(), -30));
	const [to, setTo] = useState<Date | null>(() => new Date());

	return (
		<div className="space-y-6">
			<FilterBar>
				<FilterBar.Item label="Date">
					<DatePickerField label="From" hideLabel required value={from} onChange={setFrom} />
					<FilterBar.Separator />
					<DatePickerField label="To" hideLabel required value={to} onChange={setTo} />
				</FilterBar.Item>
			</FilterBar>
			{from && to && (
				<SuspenseWrapper fullScreen={false} name="referralReport">
					<ReferralReportResults from={from} to={to} />
				</SuspenseWrapper>
			)}
		</div>
	);
}
